import { useState, useEffect, useLayoutEffect } from "react";
import { base44 } from "@/api/base44Client";

// Module-level cache: fetch all active PageMeta records once per session.
// Subsequent navigations within the SPA read from this cache without
// making another API call. A full page reload (which crawlers do) re-fetches.
let pageMetaCache = null;
let pageMetaFetchPromise = null;

async function fetchPageMetas() {
  if (pageMetaCache) return pageMetaCache;
  if (pageMetaFetchPromise) return pageMetaFetchPromise;
  pageMetaFetchPromise = base44.entities.PageMeta.filter({ is_active: true })
    .then((results) => {
      pageMetaCache = results || [];
      return pageMetaCache;
    })
    .catch(() => {
      pageMetaCache = [];
      return [];
    });
  return pageMetaFetchPromise;
}

function setMeta(attr, key, content) {
  if (!content) return;
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

/**
 * Lightweight SEO helper for a Base44 SPA — sets the document title and the
 * standard meta tags (description, Open Graph, Twitter, canonical) plus an
 * optional JSON-LD structured-data block.
 *
 * If a PageMeta record exists in the database for the current URL path and is
 * active, its title and description override the prop values. This lets super
 * admins manage SEO from the admin panel without code changes.
 */
export default function Seo({ title, description, image, imageAlt, url, articleMeta, jsonLd }) {
  const [override, setOverride] = useState(null);

  useEffect(() => {
    const path = window.location.pathname;
    let cancelled = false;
    // Defer the PageMeta DB fetch to browser idle time — it only overrides
    // meta tags (title/description), not visible content, so it doesn't need
    // to compete with critical rendering resources on the critical path.
    const run = () => {
      if (cancelled) return;
      fetchPageMetas().then((metas) => {
        if (cancelled) return;
        const match = metas.find((m) => m.path === path);
        if (match) setOverride(match);
      });
    };
    if (typeof window.requestIdleCallback === "function") {
      window.requestIdleCallback(run, { timeout: 3000 });
    } else {
      setTimeout(run, 1500);
    }
    return () => { cancelled = true; };
  }, []);

  // Blanket rule: every page title ends with " - Digital24.com" unless it
  // already contains it. This covers dynamic pages (outlet details,
  // newsroom posts, guides) that pass their own title without the suffix.
  const rawTitle = override?.title || title;
  const finalTitle = rawTitle
    ? rawTitle.includes("Digital24.com")
      ? rawTitle
      : `${rawTitle} - Digital24.com`
    : rawTitle;
  const finalDescription = override?.description || description;

  useLayoutEffect(() => {
    if (finalTitle) document.title = finalTitle;

    if (finalDescription) {
      setMeta("name", "description", finalDescription);
      setMeta("property", "og:description", finalDescription);
      setMeta("name", "twitter:description", finalDescription);
    }
    if (finalTitle) {
      setMeta("property", "og:title", finalTitle);
      setMeta("name", "twitter:title", finalTitle);
    }
    if (image) {
      setMeta("property", "og:image", image);
      setMeta("name", "twitter:image", image);
    }
    if (imageAlt) {
      setMeta("property", "og:image:alt", imageAlt);
      setMeta("name", "twitter:image:alt", imageAlt);
    }
    setMeta("property", "og:type", "article");
    setMeta("property", "og:site_name", "Digital24");
    setMeta("name", "twitter:card", "summary_large_image");
    if (url) {
      setMeta("property", "og:url", url);
    }
    if (articleMeta) {
      if (articleMeta.published_time) setMeta("property", "article:published_time", articleMeta.published_time);
      if (articleMeta.modified_time) setMeta("property", "article:modified_time", articleMeta.modified_time);
      if (articleMeta.author) setMeta("property", "article:author", articleMeta.author);
      if (articleMeta.section) setMeta("property", "article:section", articleMeta.section);
    }

    let canonical = document.head.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.setAttribute("rel", "canonical");
      document.head.appendChild(canonical);
    }
    if (url) canonical.setAttribute("href", url);

    const multiMetas = [];
    if (articleMeta && Array.isArray(articleMeta.tags)) {
      articleMeta.tags.forEach((tag) => {
        if (!tag) return;
        const el = document.createElement("meta");
        el.setAttribute("property", "article:tag");
        el.setAttribute("content", tag);
        el.setAttribute("data-seo-multi", "true");
        document.head.appendChild(el);
        multiMetas.push(el);
      });
    }

    const scripts = [];
    const ldItems = Array.isArray(jsonLd) ? jsonLd : jsonLd ? [jsonLd] : [];
    ldItems.forEach((item) => {
      if (!item) return;
      const s = document.createElement("script");
      s.type = "application/ld+json";
      s.text = JSON.stringify(item);
      document.head.appendChild(s);
      scripts.push(s);
    });

    return () => {
      scripts.forEach((s) => s.remove());
      multiMetas.forEach((el) => el.remove());
    };
  }, [finalTitle, finalDescription, image, imageAlt, url, articleMeta, jsonLd]);

  return null;
}